import { lazy, Suspense, useState, useMemo, useEffect } from 'react';
import { useDays, useSessions, useSessionStats } from './hooks/useApi';
import { useLocalStorage } from './hooks/useLocalStorage';
import { fmtDate } from './utils/format';
import Header from './components/Header';
import BaanDayPicker from './components/BaanDayPicker';
import SessionCard from './components/SessionCard';
import EmptyState from './components/EmptyState';
import BottomNav from './components/BottomNav';
import FollowingView from './components/FollowingView';
import ResetPasswordView from './components/ResetPasswordView';
import CoachAcceptView from './components/CoachAcceptView';
import WedstrijdFeedbackModal from './components/WedstrijdFeedbackModal';
import SkeletonCard from './components/SkeletonCard';
import StravaCapacityNotice from './components/StravaCapacityNotice';
import { Icon, useToast, Button, Segmented, Menu, MenuItem, MenuLabel } from './components/ui';
import { TrackConfigProvider } from './contexts/TrackConfig';
import { AuthProvider } from './contexts/Auth';
import { useAuth } from './contexts/auth-context';
import Spinner from './components/Spinner';
import { BaanWelcome, BaanPersonalShortcut, BaanSidebar } from './components/BaanWelcome';
import { activeSessions, amsterdamDay } from './utils/activity';
import { matchesSessionSearch } from './utils/dateNavigation';
import './styles/global.scss';
import './styles/performance.scss';

const TransponderView = lazy(() => import('./components/TransponderView'));
const SeasonView = lazy(() => import('./components/SeasonView'));
const AdminView = lazy(() => import('./components/AdminView'));
const ProfileView = lazy(() => import('./components/ProfileView'));
const RankingView = lazy(() => import('./components/RankingView'));
const LiveView = lazy(() => import('./components/LiveView'));
const CoachView = lazy(() => import('./components/CoachView'));

// ── URL routing ──────────────────────────────────────────────────────────────
// "Volgend" is no longer a top-level view: it is a filter on the Baan overview,
// because it showed the same thing (a list of sessions) as the day list. The old
// /volgend path still resolves — it lands on Baan with the filter applied.
const VIEW_PATHS = {
  home: '/', transponder: '/', live: '/live',
  ranking: '/ranglijst', myseason: '/activiteiten', profile: '/profiel', admin: '/admin', coach: '/coach',
};
const PATH_VIEWS = {
  '/': 'home', '/live': 'live', '/ranglijst': 'ranking', '/volgend': 'home',
  '/activiteiten': 'myseason', '/profiel': 'profile', '/admin': 'admin', '/coach': 'coach',
};
// Aparte paden met een eigen render-tak (buiten de view-router om).
const SPECIAL_PATHS = new Set(['/reset-password', '/coach-invite']);

// OAuth-koppel-redirects van strava-callback/polar-callback/garmin-callback;
// elk stuurt ?<provider>Linked=1&transponder=X of ?<provider>Error=1.
const OAUTH_PROVIDERS = [
  { key: 'strava', name: 'Strava', hash: '#strava-auto-export' },
  { key: 'polar',  name: 'Polar',  hash: '#profiel-koppelingen' },
  { key: 'garmin', name: 'Garmin', hash: '#profiel-koppelingen' },
];
const OAUTH_REDIRECT_PARAMS = OAUTH_PROVIDERS.flatMap(({ key }) => [`${key}Linked`, `${key}Error`]);

function buildAppUrl(view, transponder, date, scope) {
  const path = VIEW_PATHS[view] ?? '/';
  const p    = new URLSearchParams();
  if (view === 'transponder' && transponder) p.set('transponder', transponder);
  if ((view === 'home' || view === 'transponder') && date) p.set('datum', date);
  if (view === 'home' && scope === 'following') p.set('volgend', '1');
  const qs = p.toString();
  return qs ? `${path}?${qs}` : path;
}

function parseAppUrl() {
  const { pathname, search } = window.location;
  const p = new URLSearchParams(search);
  // Don't parse URL on OAuth-koppel-redirects (Strava/Polar/Garmin) – handled
  // separately, anders zou de meegestuurde transponder dit laten landen op de
  // sessie van vandaag (vaak zonder activiteit) in plaats van op het profiel.
  if (OAUTH_REDIRECT_PARAMS.some(k => p.has(k))) {
    return { view: 'home', transponder: null, date: null, scope: 'all' };
  }
  const pathView   = PATH_VIEWS[pathname] ?? (SPECIAL_PATHS.has(pathname) ? 'home' : 'notfound');
  const transponder = p.get('transponder') || null;
  const date        = p.get('datum') || p.get('date') || null;
  // /volgend keeps working as a bookmark: it resolves to Baan with the filter on.
  const scope       = pathname === '/volgend' || p.get('volgend') === '1' ? 'following' : 'all';
  return {
    view: pathView === 'home' && transponder ? 'transponder' : pathView,
    transponder,
    date,
    scope,
  };
}

function isCompetitionDay(date, sessions) {
  if (!date || !sessions.length) return false;
  const [y, m, d] = date.split('-').map(Number);
  const dow = new Date(y, m - 1, d).getDay(); // 0=zon, 6=za
  if (dow !== 0 && dow !== 6) return false;
  return sessions.some(s => {
    const parts = new Intl.DateTimeFormat('nl-NL', {
      timeZone: 'Europe/Amsterdam', hour: 'numeric', minute: 'numeric', hour12: false,
    }).formatToParts(new Date(s.firstPassing));
    const h     = parseInt(parts.find(p => p.type === 'hour').value);
    const min   = parseInt(parts.find(p => p.type === 'minute').value);
    const total = h * 60 + min;
    return dow === 6
      ? total >= 18 * 60 + 30 && total < 23 * 60  // za 18:30–23:00
      : total >= 17 * 60 + 30 && total < 23 * 60; // zo 17:30–23:00
  });
}

export default function App() {
  const _init = parseAppUrl();
  const isResetPage       = window.location.pathname === '/reset-password';
  const isCoachInvitePage = window.location.pathname === '/coach-invite';
  const [view, setView]                       = useState(_init.view);
  const [selectedTransponder, setTransponder] = useState(_init.transponder);
  const [selectedDate, setDate]               = useState(_init.date);
  const [scope, setScope]                     = useState(_init.scope ?? 'all');
  const [lane, setLane]                      = useLocalStorage('selectedLane', 'binnenbaan');
  const toast = useToast();
  const [searchQuery, setSearchQuery]         = useState('');
  const [sortBy, setSortBy]                   = useState('start');
  const [feedbackDate, setFeedbackDate]       = useState(null);
  const [clock, setClock] = useState(Date.now);

  const { data: daysData, loading: daysLoading, error: daysError, refetch: refetchDays } = useDays();
  const days = useMemo(() => daysData?.days ?? [], [daysData]);

  // OAuth-koppel-redirect afhandelen (Strava/Polar/Garmin): toon een toast en
  // stuur terug naar het profiel in plaats van de sessie van vandaag te openen
  // (die vaak geen activiteit heeft als iemand net koppelt, niet schaatst).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    for (const { key, name, hash } of OAUTH_PROVIDERS) {
      if (params.get(`${key}Linked`) === '1') {
        const t = params.get('transponder');
        if (t) setTransponder(t);
        setView('profile');
        window.history.replaceState({}, '', `/profiel${hash}`);
        toast(`${name} gekoppeld!`);
        return;
      }
      if (params.get(`${key}Error`) === '1') {
        toast(`Koppelen met ${name} mislukt. Probeer opnieuw.`, { type: 'error' });
        window.history.replaceState({}, '', '/');
        return;
      }
    }
  }, [toast]);

  // Browser terug/vooruit
  useEffect(() => {
    function onPop() {
      const { view: v, transponder: t, date: d, scope: s } = parseAppUrl();
      setView(v);
      setTransponder(t);
      setScope(s ?? 'all');
      setDate(d);
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Stel de meest recente dag in zodra de lijst geladen is
  useEffect(() => {
    if (days.length && !selectedDate) setDate(days[0]);
  }, [days, selectedDate]);

  const effectiveDate = selectedDate ?? days[0] ?? null;

  const { data: sessionsData, loading: sessionsLoading, error: sessionsError, updatedAt, refetch: refetchSessions } = useSessions(effectiveDate);
  const sessions = useMemo(() => sessionsData?.sessions ?? [], [sessionsData]);

  const { data: statsData, error: statsError, refetch: refetchStats } = useSessionStats(effectiveDate);
  const statsMap = statsData?.stats ?? {};
  const overviewError = daysError || sessionsError;
  const isToday = effectiveDate === amsterdamDay(clock);
  const activeCount = isToday ? activeSessions(sessions, clock).length : 0;
  const stale = isToday && updatedAt > 0 && clock - updatedAt > 45_000;

  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const titles = { home: 'Baan', live: 'Live & terugkijken', ranking: 'Ranglijst', myseason: 'Mijn ritten', profile: 'Mijn profiel', coach: 'Coachwerkplek', transponder: 'Rit & wedstrijdanalyse', admin: 'Beheer' };
    document.title = `${isResetPage ? 'Wachtwoord instellen' : isCoachInvitePage ? 'Coach-uitnodiging' : titles[view] ?? 'Pagina niet gevonden'} | BVA Schaatsen`;
  }, [view, isResetPage, isCoachInvitePage]);

  const [refreshing, setRefreshing] = useState(false);
  async function handleRefresh() {
    setRefreshing(true);
    try {
      const results = await Promise.all([refetchDays(), ...(effectiveDate ? [refetchSessions(), refetchStats()] : [])]);
      if (results.some(result => result?.error)) {
        toast('Niet alle gegevens konden worden vernieuwd. Probeer het opnieuw.', { type: 'error' });
      }
    } finally {
      setRefreshing(false);
    }
  }

  const dayQuality = useMemo(() => {
    const scores = sessions.map(s => s.iceQuality).filter(q => q != null);
    if (!scores.length) return { avg: null, count: 0 };
    const avg = Math.round(scores.reduce((s, q) => s + q, 0) / scores.length * 10) / 10;
    return { avg, count: scores.length };
  }, [sessions]);

  const feedbackEligible = !isResetPage && !isCoachInvitePage
    && view === 'transponder' && !!selectedTransponder
    && isCompetitionDay(effectiveDate, sessions.filter(s => s.transponder === selectedTransponder));

  useEffect(() => {
    setFeedbackDate(feedbackEligible && !localStorage.getItem(`feedback_${effectiveDate}`)
      ? effectiveDate : null);
  }, [effectiveDate, feedbackEligible]);

  function dismissFeedback() {
    localStorage.setItem(`feedback_${feedbackDate}`, '1');
    setFeedbackDate(null);
  }

  const filteredSessions = useMemo(() => {
    const list = sessions.filter(session => matchesSessionSearch(session, searchQuery));
    return [...list].sort((a, b) => {
      if (sortBy === 'name') {
        const na = (a.nickname || a.transponder).toLowerCase();
        const nb = (b.nickname || b.transponder).toLowerCase();
        return na.localeCompare(nb);
      }
      if (sortBy === 'laps') return (statsData?.stats?.[b.transponder]?.lapCount ?? 0) - (statsData?.stats?.[a.transponder]?.lapCount ?? 0);
      return new Date(b.firstPassing) - new Date(a.firstPassing);
    });
  }, [sessions, searchQuery, sortBy, statsData]);

  function navigate(target, { transponder: t, date: d } = {}) {
    const newT = t !== undefined ? (t ?? null) : (target === 'transponder' ? selectedTransponder : null);
    const newD = d !== undefined ? (d ?? null) : effectiveDate;
    setView(target);
    setTransponder(newT);
    if (d !== undefined) setDate(newD);
    window.history.pushState({}, '', buildAppUrl(target, newT, newD, scope));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  // Baan filter: "Iedereen" (the day's sessions) vs "Volgend" (recent rides of
  // skaters you follow). Replaces the separate /volgend view.
  function changeScope(next) {
    setScope(next);
    window.history.replaceState({}, '', buildAppUrl('home', null, effectiveDate, next));
  }

  return (
    <TrackConfigProvider>
    <AuthProvider
      onAuthenticated={() => { if (!isResetPage && !isCoachInvitePage) navigate('profile'); }}
      onImpersonationEnd={() => navigate('admin')}
    >
      <PreferenceLaneSync onLaneChange={setLane} />
      <ImpersonationBanner />
      <Header view={view} onNavigate={v => navigate(v)} />
      <StravaCapacityNotice />

      <BottomNav view={view} onNavigate={v => navigate(v)} />

      {feedbackEligible && feedbackDate === effectiveDate && (
        <WedstrijdFeedbackModal
          key={feedbackDate}
          date={feedbackDate}
          onClose={dismissFeedback}
        />
      )}

      <main id="main" className="app-main mx-auto px-5 main-with-bottom-nav">
        <Suspense fallback={<Spinner text="Pagina laden..." />}>
        {isResetPage ? <ResetPasswordView /> : isCoachInvitePage ? <CoachAcceptView /> : (<>
        {view === 'live'     && <LiveView />}
        {view === 'myseason' && <SeasonView onNavigate={v => navigate(v)} />}
        {view === 'admin'    && <AdminView />}
        {view === 'coach'    && <CoachView onNavigate={navigate} />}
        {view === 'profile'  && <ProfileView onNavigate={navigate} />}
        {view === 'ranking'  && (
          <RankingView
            onViewTransponder={(transponder, date) => navigate('transponder', { transponder, date: date || effectiveDate })}
          />
        )}
        {view === 'transponder' && selectedTransponder && (
          <TransponderView
            transponder={selectedTransponder}
            date={effectiveDate}
            onDateChange={date => navigate('transponder', { transponder: selectedTransponder, date })}
            onBack={() => window.history.back()}
          />
        )}

        {view === 'notfound' && (
          <EmptyState
            icon={<Icon name="compass" size={44} />}
            title="Pagina niet gevonden"
            sub="Deze pagina bestaat niet (meer). Ga terug naar het overzicht."
            action={
              <button onClick={() => navigate('home')} className="mt-5 bg-accent border-none rounded-[20px] text-[#fff] py-[9px] px-5 cursor-pointer font-display text-[14px] font-semibold tracking-[0.05em]">
                Naar overzicht
              </button>
            }
          />
        )}

        {view === 'home' && (
          <>
            <BaanWelcome onNavigate={navigate} />
            <BaanPersonalShortcut sessions={sessions} date={effectiveDate} onNavigate={navigate} />
            <div className="baan-layout">
            <section className="min-w-0" aria-label="Ritten op de baan">
            {/* ── Kop + bediening ────────────────────────────────────────
                Dit waren vijf gestapelde regels (datum, titel, teller, filter,
                zoeken+sorteren) voordat je de eerste schaatser zag — op een
                telefoon bijna het hele scherm. Nu twee regels:

                  1. datum als titel, met een icoonknop om hem te wijzigen en
                     één om te verversen; de teller en ijskwaliteit staan als
                     korte meta-regel eronder in plaats van als losse pillen.
                  2. zoeken, met sorteren in een menu achter één knop.

                De Iedereen/Volgend-filter verschijnt alleen als je bent
                ingelogd, en staat tegen de lijst aan waar hij op slaat. */}
            {effectiveDate && (
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <h2 className="baan-date min-w-0 flex-1 font-heading tracking-[-0.01em] text-ink">
                    <span className="mb-1 block text-[12px] font-semibold uppercase tracking-[0.1em] text-ink-3">{isToday ? 'Vandaag op De Meent' : 'Baanarchief'}</span>
                    {fmtDate(effectiveDate)}
                  </h2>
                  {days.length > 0 && (
                    <BaanDayPicker
                      selectedDay={effectiveDate}
                      scope={scope}
                      query={searchQuery}
                      onChange={day => {
                        setDate(day);
                        setTransponder(null);
                        window.history.replaceState({}, '', buildAppUrl('home', null, day, scope));
                      }}
                    />
                  )}
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10"
                    onClick={handleRefresh}
                    disabled={refreshing}
                    aria-label="Gegevens vernieuwen"
                  >
                    <Icon name="refresh" size={18} className={refreshing ? 'animate-[spin_0.8s_linear_infinite]' : undefined} />
                  </Button>
                </div>

                <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[14px] text-ink-3">
                  <span>
                    {sessions.length} {sessions.length === 1 ? 'schaatser' : 'schaatsers'} {isToday ? 'vandaag' : 'op deze dag'}
                  </span>
                  {isToday && !overviewError && <span>· {activeCount} recent actief</span>}
                  {dayQuality.avg != null && dayQuality.count > 0 && (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>IJs {dayQuality.avg.toFixed(1)}/10</span>
                    </>
                  )}
                </p>
              </div>
            )}

            {/* Iedereen / Volgend — staat direct boven de lijst waar hij op slaat. */}
            <ScopeFilter value={scope} onChange={changeScope} />

            {scope === 'all' && (<>
            {(overviewError || stale) && (
              <div className="connection-note mb-4" data-error={!!overviewError} role={overviewError ? 'alert' : 'status'}>
                <span>
                  {overviewError ? 'De verbinding met de baan is onderbroken.' : 'De gegevens zijn mogelijk niet meer actueel.'}
                  {sessions.length > 0 ? ' Je ziet de laatst opgehaalde ritten.' : ' We kunnen de ritten nu niet laden.'}
                </span>
                <Button size="sm" onClick={handleRefresh} disabled={refreshing}>Opnieuw proberen</Button>
              </div>
            )}
            {statsError && sessions.length > 0 && (
              <p role="alert" className="mb-3 rounded-md border border-red-500/25 bg-red-100 p-3 text-[14px] text-red-600">
                Rondetijden konden niet worden bijgewerkt. {statsData ? 'Je ziet de laatst opgehaalde tijden.' : 'Open een rit of probeer opnieuw te vernieuwen.'}
              </p>
            )}
            {(daysLoading || sessionsLoading) && sessions.length === 0 && (
              <div role="status" aria-label="Ritten laden">
                <span className="sr-only">Ritten laden...</span>
                <div className="flex flex-col gap-2" aria-hidden="true">
                  {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)}
                </div>
              </div>
            )}

            {!daysLoading && !overviewError && days.length === 0 && (
              <EmptyState
                icon={<Icon name="snowflake" size={44} />}
                title="Het ijs wacht op de eerste ronde"
                sub="Zodra er passages worden geregistreerd, vind je hier de ritten en rondetijden."
                action={<Button className="mt-4" onClick={handleRefresh} disabled={refreshing}>Controleer op ritten</Button>}
              />
            )}
            {!daysLoading && !sessionsLoading && !overviewError && effectiveDate && sessions.length === 0 && (
              <EmptyState
                icon={<Icon name="calendar" size={40} />}
                title="Geen ritten op deze dag"
                sub="Kies een andere datum om eerdere ritten te ontdekken."
                action={days[0] && days[0] !== effectiveDate ? <Button className="mt-4" onClick={() => {
                  setDate(days[0]);
                  window.history.replaceState({}, '', buildAppUrl('home', null, days[0], scope));
                }}>Bekijk de laatste schaatsdag</Button> : null}
              />
            )}
            </>)}

            {/* Zoekbalk + sortering */}
            {(
              <div className="mb-3 flex items-center gap-2">
                <div className="relative min-w-0 flex-1">
                  <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-3">
                    <Icon name="search" size={16} />
                  </span>
                  <label htmlFor="session-search" className="sr-only">Zoek schaatser</label>
                  <input
                    id="session-search"
                    type="search"
                    placeholder="Zoek naam of transponder..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="h-12 w-full rounded-md border border-line bg-surface pl-10 pr-12 font-body text-[16px] text-ink outline-none placeholder:text-ink-3 focus-visible:border-primary-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-0.5 top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-ink-3 transition-colors duration-150 hover:bg-surface-3 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                      aria-label="Zoekopdracht wissen"
                    >
                      <Icon name="close" size={15} />
                    </button>
                  )}
                </div>
                {/* Sorteren nam als pilgroep een halve regel; als menu is het
                    één knop, en de gekozen volgorde staat in het label. */}
                {scope === 'all' && <Menu
                  trigger={
                    <Button variant="outline" size="icon" className="h-11 w-11" aria-label={`Sorteren op ${SORT_LABELS[sortBy]}`}>
                      <SortIcon />
                    </Button>
                  }
                >
                  <MenuLabel>Sorteren op</MenuLabel>
                  {Object.entries(SORT_LABELS).map(([key, label]) => (
                    <MenuItem
                      key={key}
                      icon={sortBy === key ? <Icon name="check" size={15} /> : <span />}
                      onSelect={() => setSortBy(key)}
                    >
                      {label}
                    </MenuItem>
                  ))}
                </Menu>}
              </div>
            )}

            {scope === 'following' && (
              <FollowingView
                date={effectiveDate}
                query={searchQuery}
                onViewTransponder={(transponder, date) =>
                  navigate('transponder', { transponder, date: date || effectiveDate })
                }
              />
            )}

            {scope === 'all' && (<>
            {searchQuery.trim() && (
              <div className="mb-2 text-[13px] text-ink-3" role="status" aria-live="polite">
                {filteredSessions.length} van {sessions.length} schaatsers
              </div>
            )}

            {/* Sessiekaarten */}
            <div className="flex flex-col gap-2">
              {filteredSessions.map(session => (
                <SessionCard
                  key={session.transponder}
                  session={session}
                  stats={statsMap[session.transponder] ?? null}
                  lane={lane}
                  isActive={session.transponder === selectedTransponder && view === 'transponder'}
                  onClick={() => navigate('transponder', { transponder: session.transponder, date: effectiveDate })}
                />
              ))}
              {sessions.length > 0 && filteredSessions.length === 0 && (
                <div className="px-4 py-6 text-center text-[14px] text-ink-3">
                  Geen schaatsers gevonden voor “{searchQuery}”
                </div>
              )}
            </div>
            {updatedAt > 0 && (
              <p className="mt-4 text-[13px] text-ink-3">
                Bijgewerkt om {new Date(updatedAt).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' })}.
                {isToday && ' Actief betekent: een passage in de laatste 5 minuten.'}
              </p>
            )}
            </>)}
            </section>
            <BaanSidebar sessions={sessions} statsMap={statsMap} date={effectiveDate} error={overviewError} onNavigate={navigate} />
            </div>
          </>
        )}
        </>)}
        </Suspense>
      </main>
    </AuthProvider>
    </TrackConfigProvider>
  );
}

const SORT_LABELS = { start: 'Tijd', laps: 'Ronden', name: 'Naam' };

function SortIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7h12M4 12h9M4 17h6" />
      <path d="M18 10l3 3-3 3" />
    </svg>
  );
}

// De Iedereen/Volgend-filter zit onder AuthProvider, dus dit kleine kind-component
// mag useAuth() gebruiken (App zelf rendert de provider en kan dat niet).
function ScopeFilter({ value, onChange }) {
  const { loggedIn } = useAuth();
  if (!loggedIn) return null;
  return (
    <div className="mb-4">
      <Segmented
        ariaLabel="Welke schaatsers"
        value={value}
        onChange={onChange}
        options={[
          { value: 'all', label: 'Iedereen' },
          { value: 'following', label: 'Volgend' },
        ]}
      />
    </div>
  );
}

function PreferenceLaneSync({ onLaneChange }) {
  const { loggedIn, preferences } = useAuth();
  useEffect(() => {
    if (loggedIn && preferences?.defaultLane) onLaneChange(preferences.defaultLane);
  }, [loggedIn, preferences?.defaultLane, onLaneChange]);
  return null;
}

// Zichtbaar zolang een admin via /admin → Gebruikers → "Inloggen als" een ander
// account bekijkt (zie contexts/Auth.jsx). Maakt het onmogelijk om per ongeluk
// te vergeten dat je niet in je eigen account zit, en geeft een snelle weg terug.
function ImpersonationBanner() {
  const { impersonating, user, stopImpersonating } = useAuth();
  if (!impersonating) return null;
  return (
    <div className="sticky top-0 z-[101] flex flex-wrap items-center justify-center gap-2 bg-[var(--c-gold)] px-4 py-2 text-center text-[13.5px] font-display font-semibold tracking-[0.02em] text-[#1a1a1a]">
      <span>Je bekijkt de app als {user?.name || user?.email || 'een ander account'}</span>
      <button
        onClick={stopImpersonating}
        className="cursor-pointer rounded-md border border-[#1a1a1a44] bg-[#ffffff55] px-[10px] py-[3px] font-display text-[13px] font-bold tracking-[0.02em] text-[#1a1a1a]"
      >
        Terug naar admin
      </button>
    </div>
  );
}
